import React, { useMemo, memo } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Grid from '@mui/material/Grid';
import RouterIcon from '@mui/icons-material/Router';
import { NavLink as RouterLink } from 'react-router';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

import { DataTreeItem, SensorLatest, VarName } from '../../services/api';
import { useAppSelector } from '../../state/store';
import { getSensorsById } from '../../state/selectors';
import { knownOfflineTag } from './helpers';
import { ListOptions } from '../../Views/OfflineView';
import { sensorTypeDetails } from '../../utils/sensorProperties';
import SensorIcon from '../../styles/icons/SensorIcon';
import { getShortTimeStr } from '../../utils/functions';
import { themeProps } from '../../styles/theme';

interface OfflineGatewaySummaryProps {
  allSensors: DataTreeItem[];
  isLocOffline: boolean;
  listType: ListOptions;
  handleTagging: (sensors: DataTreeItem[], isTagged: boolean) => void;
  offlineGateways: string[];
}

interface GatewayRowData {
  gatewayId: string;
  gatewayDetails: SensorLatest | undefined;
  gatewaySensors: DataTreeItem[];
  gatewayDataItem: DataTreeItem;
  allTreeItem: DataTreeItem[];
  isTagged: boolean;
}

// memoized so rows unaffected by a given re-render don't redo their nested-list work
// (needs `import { memo } from 'react'` if not already imported in this file)
const GatewayRow = memo(
  ({
    row,
    handleTagging,
  }: {
    row: GatewayRowData;
    handleTagging: (sensors: DataTreeItem[], isTagged: boolean) => void;
  }) => {
    const sensorsById = useAppSelector(getSensorsById);
    const { gatewayId, gatewayDetails, gatewaySensors, gatewayDataItem, allTreeItem, isTagged } =
      row;

    return (
      <Grid container sx={{ marginTop: '10px', alignItems: 'center' }}>
        <Grid size={{ sm: 1 }}>
          <RouterIcon color="error" sx={{ cursor: 'pointer' }} />
        </Grid>
        <Grid size={{ sm: 3 }}>
          <Tooltip title={`Go to ${gatewayDetails?.name} config page`} placement="top-start">
            <Link component={RouterLink} to={`/sensors/${encodeURIComponent(gatewayId)}`}>
              <ListItemText>{gatewayDetails?.name}</ListItemText>
            </Link>
          </Tooltip>
        </Grid>
        <Grid size={{ sm: 2 }} sx={{ cursor: 'pointer' }}>
          {gatewaySensors.length > 0 ? (
            <Tooltip
              title={
                <Box>
                  <List>
                    {gatewaySensors.map((offlineSensor) => {
                      const sensorDetails = sensorsById.get(offlineSensor.id);
                      const Icon =
                        (sensorDetails?.type && sensorTypeDetails[sensorDetails.type]?.icon) ??
                        SensorIcon;
                      return (
                        <ListItem sx={{ width: '500px' }} key={offlineSensor.id}>
                          <Grid container spacing={2} sx={{ padding: '10px', width: '100%' }}>
                            <Grid size={{ sm: 1 }}>
                              <Icon color="error" />
                            </Grid>
                            <Grid size={{ sm: 3 }} sx={{ alignSelf: 'center' }}>
                              <Link
                                component={RouterLink}
                                to={`/sensors/${encodeURIComponent(offlineSensor.id)}`}
                              >
                                <ListItemText>{sensorDetails?.name}</ListItemText>
                              </Link>
                            </Grid>
                            <Grid size={{ sm: 3 }} sx={{ alignSelf: 'center' }}>
                              {getShortTimeStr(offlineSensor.time ?? 0)}
                            </Grid>
                          </Grid>
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              }
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <span
                  style={{
                    background: themeProps.colors.warningRed,
                    padding: '5px 10px',
                    borderRadius: '20px',
                    color: themeProps.colors.white,
                  }}
                >
                  <b>{gatewaySensors.length + 1} offline</b>
                </span>
                <InfoOutlined sx={{ marginLeft: '5px' }} />
              </Box>
            </Tooltip>
          ) : (
            <span style={{ marginLeft: '20px' }}>-</span>
          )}
        </Grid>
        <Grid size={{ sm: 2 }} sx={{ textAlign: 'center' }}>
          <span>- </span>
        </Grid>
        <Grid size={{ sm: 2 }} sx={{ cursor: 'pointer', textAlign: 'center' }}>
          <Tooltip title="Last seen" placement="top-start">
            <span>{getShortTimeStr(gatewayDataItem.time ?? 0)}</span>
          </Tooltip>
        </Grid>

        <Grid size={{ sm: 2 }} sx={{ textAlign: 'end' }}>
          <Button
            onClick={() => handleTagging(allTreeItem, isTagged)}
            variant={themeProps.btnVariant.default}
          >
            {isTagged ? 'Tagged' : 'Click to Tag'}
          </Button>
        </Grid>
      </Grid>
    );
  }
);

function OfflineGatewaySummary({
  allSensors,
  isLocOffline,
  listType,
  handleTagging,
  offlineGateways,
}: OfflineGatewaySummaryProps) {
  const sensorsById = useAppSelector(getSensorsById);

  // Precompute each gateway's derived data once instead of re-scanning allSensors
  // (a filter + two separate finds) on every render, for every gateway.
  const gatewayRows = useMemo<GatewayRowData[]>(
    () =>
      offlineGateways
        .map((gatewayId) => {
          const gatewayDetails = sensorsById.get(gatewayId);
          const gatewaySensors = allSensors.filter(
            (sensor) => sensorsById.get(sensor.id)?.gateway === gatewayId
          );
          const gatewayDataItem: DataTreeItem = allSensors.find(
            (item) => item.id === gatewayId
          ) ?? {
            id: gatewayId,
            varName: VarName.OnlineStatus,
          };
          const allTreeItem = [...gatewaySensors, gatewayDataItem];
          const isTagged = allTreeItem.some((sensor) =>
            sensorsById.get(sensor.id)?.tags?.includes(knownOfflineTag)
          );

          return {
            gatewayId,
            gatewayDetails,
            gatewaySensors,
            gatewayDataItem,
            allTreeItem,
            isTagged,
          };
        })
        // Hide tagged gateways when showing the untagged list
        .filter((row) => !(row.isTagged && listType === ListOptions.untagged)),
    [offlineGateways, allSensors, sensorsById, listType]
  );

  // Early return if no offline gateways or location is offline
  if (offlineGateways.length === 0 || isLocOffline) {
    return null;
  }

  return (
    <Box>
      {gatewayRows.map((row) => (
        <GatewayRow key={row.gatewayId} row={row} handleTagging={handleTagging} />
      ))}
    </Box>
  );
}

export default OfflineGatewaySummary;
